import { Grid, Box } from "@mui/material";
import styles from "./AddFarmBillingEntity.module.scss";
import InputTextField from "../../components/TextField";
import Dropdown from "../../components/DropDown";
import { customStyle } from "../../styles/serviceAgreement";
import { useAddFarm } from "../../hooks/useAddFarmBillingEntity";
import { useState } from "react";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";

export const RenameEntities = () => {
  const {
    farmNameOptions,
    billingEntityOptions,
    selectedLandlordToRename,
    setSelectedLandlordToRename,
    newLandlordName,
    setNewLandlordName,
    renameLandlordError,
    setRenameLandlordError,
    handleRenameLandlord,
    selectedBillingEntityToRename,
    setSelectedBillingEntityToRename,
    newBillingEntityName,
    setNewBillingEntityName,
    renameBillingEntityError,
    setRenameBillingEntityError,
    handleRenameBillingEntity,
  } = useAddFarm();

  const [showLandlordConfirm, setShowLandlordConfirm] = useState(false);
  const [showBillingConfirm, setShowBillingConfirm] = useState(false);

  const onLandlordRenameClick = () => {
    if (!selectedLandlordToRename) {
      setRenameLandlordError("Landlord is required");
      return;
    }
    if (!newLandlordName.trim()) {
      setRenameLandlordError("New name is required");
      return;
    }
    setRenameLandlordError(null);
    setShowLandlordConfirm(true);
  };

  const onBillingEntityRenameClick = () => {
    if (!selectedBillingEntityToRename) {
      setRenameBillingEntityError("Billing Entity is required");
      return;
    }
    if (!newBillingEntityName.trim()) {
      setRenameBillingEntityError("New name is required");
      return;
    }
    setRenameBillingEntityError(null);
    setShowBillingConfirm(true);
  };

  return (
    <div className={styles.formContainer}>
      <Grid container spacing={4}>
        {/* Rename Landlord */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={5}>
              <Dropdown
                label="Select Landlord"
                value={selectedLandlordToRename}
                options={farmNameOptions}
                customStyles={{
                  ...customStyle,
                  menuList: (provided: any) => ({
                    ...provided,
                    maxHeight: "100px",
                  }),
                }}
                isClearable
                isDisabled={false}
                placeholder="Select Landlord"
                onChange={(option: any) => {
                  setSelectedLandlordToRename(option);
                  setRenameLandlordError(null);
                }}
                error={!!renameLandlordError && !selectedLandlordToRename}
                helperText={
                  !selectedLandlordToRename ? renameLandlordError ?? "" : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <InputTextField
                label="New Landlord Name"
                placeholder="Enter new name"
                name="newLandlordName"
                value={newLandlordName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewLandlordName(e.target.value);
                  if (e.target.value.trim()) setRenameLandlordError(null);
                }}
                className={styles.inputText}
                error={!!renameLandlordError && !!selectedLandlordToRename && !newLandlordName.trim()}
                helperText={
                  selectedLandlordToRename && !newLandlordName.trim()
                    ? renameLandlordError ?? ""
                    : ""
                }
              />
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="flex-end" height="73%">
                <button
                  className={styles.saveButton}
                  onClick={onLandlordRenameClick}
                >
                  Rename
                </button>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* Rename Billing Entity */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={5}>
              <Dropdown
                label="Select Billing Entity"
                value={selectedBillingEntityToRename}
                options={billingEntityOptions}
                customStyles={{
                  ...customStyle,
                  menuList: (provided: any) => ({
                    ...provided,
                    maxHeight: "100px",
                  }),
                }}
                isClearable
                isDisabled={false}
                placeholder="Select Billing Entity"
                onChange={(option: any) => {
                  setSelectedBillingEntityToRename(option);
                  setRenameBillingEntityError(null);
                }}
                error={
                  !!renameBillingEntityError && !selectedBillingEntityToRename
                }
                helperText={
                  !selectedBillingEntityToRename
                    ? renameBillingEntityError ?? ""
                    : ""
                }
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <InputTextField
                label="New Billing Entity Name"
                placeholder="Enter new name"
                name="newBillingEntityName"
                value={newBillingEntityName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setNewBillingEntityName(e.target.value);
                  if (e.target.value.trim()) setRenameBillingEntityError(null);
                }}
                className={styles.inputText}
                error={
                  !!renameBillingEntityError &&
                  !!selectedBillingEntityToRename &&
                  !newBillingEntityName.trim()
                }
                helperText={
                  selectedBillingEntityToRename && !newBillingEntityName.trim()
                    ? renameBillingEntityError ?? ""
                    : ""
                }
              />
            </Grid>
            <Grid item>
              <Box display="flex" alignItems="flex-end" height="73%">
                <button
                  className={styles.saveButton}
                  onClick={onBillingEntityRenameClick}
                >
                  Rename
                </button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={showLandlordConfirm}
        title="Rename Landlord"
        description={`Are you sure you want to rename "${selectedLandlordToRename?.label}" to "${newLandlordName}"?`}
        handleNo={() => setShowLandlordConfirm(false)}
        handleYes={() => {
          setShowLandlordConfirm(false);
          handleRenameLandlord();
        }}
      />

      <ConfirmationDialog
        open={showBillingConfirm}
        title="Rename Billing Entity"
        description={`Are you sure you want to rename "${selectedBillingEntityToRename?.label}" to "${newBillingEntityName}"?`}
        handleNo={() => setShowBillingConfirm(false)}
        handleYes={() => {
          setShowBillingConfirm(false);
          handleRenameBillingEntity();
        }}
      />
    </div>
  );
};
